/**
 * Card — generic container primitive with elevation, padding, and optional press.
 *
 * Feature-specific cards (VehicleCard, BookingCard) compose or extend this
 * primitive in Phase 1+. The Card itself is intentionally unopinionated
 * about internal layout.
 *
 * @see docs/milestones/M0.6-UI-PRIMITIVES.md §7
 */

import React, { useMemo } from 'react'
import { Platform, View } from 'react-native'
import Animated from 'react-native-reanimated'
import { Pressable } from './Pressable'
import { useAnimatedPress } from '../../lib/hooks/use-animated-press'
import { useTheme } from '../../lib/hooks/use-theme'
import { RADIUS, SPACING } from '../../lib/theme/tokens'
import { useSemanticColors } from '../../lib/theme/tokens'
import { WEB_SHADOWS } from '../../lib/theme/web-shadow'
import { cn } from '../../lib/theme/variants'
import { GlassSurface } from './GlassSurface'

// ─── Types ──────────────────────────────────────────────────────────────────

type CardElevation = 'none' | 'sm' | 'md' | 'lg'
type CardPadding = 'none' | 'sm' | 'md' | 'lg'
type CardRadius = 'sm' | 'md' | 'lg' | 'xl'

interface CardProps {
  /** Visual elevation / shadow level */
  elevation?: CardElevation
  /** Padding preset */
  padding?: CardPadding
  /** Border radius preset */
  radius?: CardRadius
  /** Override background color */
  backgroundColor?: string
  /** Press handler — makes card pressable with scale animation */
  onPress?: () => void
  /** Disable the press interaction */
  disabled?: boolean
  children: React.ReactNode
  /** Additional className for NativeWind overrides */
  className?: string
}

// ─── Padding Map ────────────────────────────────────────────────────────────

const PADDING_MAP = {
  none: 0,
  sm: SPACING.sm + SPACING.xs, // compact cards (badge-like)
  md: SPACING.md, // 16 — standard card padding
  lg: SPACING.lg, // 24 — generous card padding
} as const

// ─── Radius Map ─────────────────────────────────────────────────────────────

const RADIUS_MAP: Record<CardRadius, number> = {
  sm: RADIUS.sm,
  md: RADIUS.md,
  lg: RADIUS.lg,
  xl: RADIUS.xl,
}

const SHADOW_MAP: Record<CardElevation, object> = {
  none: {},
  sm: WEB_SHADOWS.sm,
  md: WEB_SHADOWS.md,
  lg: WEB_SHADOWS.lg,
}

// ─── Card ───────────────────────────────────────────────────────────────────

export function Card({
  elevation = 'sm',
  padding = 'md',
  radius = 'lg',
  backgroundColor,
  onPress,
  disabled = false,
  children,
  className,
}: CardProps) {
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)

  const { animatedStyle, onPressIn, onPressOut } = useAnimatedPress({
    scaleTo: 0.98,
    disabled: disabled || !onPress,
  })

  const solidContainerStyle = useMemo(
    () => ({
      padding: PADDING_MAP[padding],
      borderRadius: RADIUS_MAP[radius],
      backgroundColor: backgroundColor ?? colors.surface,
      borderWidth: 1,
      borderColor: colors.border,
      ...SHADOW_MAP[elevation],
    }),
    [padding, radius, backgroundColor, colors.surface, colors.border, elevation],
  )

  const glassContainerStyle = useMemo(
    () => ({
      // Android renders an elevated translucent Fabric node through a
      // separate rectangular hardware layer on some GPUs. GlassSurface
      // supplies the material edge there; keep the optional depth only where
      // it composites safely.
      ...(Platform.OS === 'android' ? {} : SHADOW_MAP[elevation]),
    }),
    [elevation],
  )

  const glassContentStyle = useMemo(
    () => ({
      padding: PADDING_MAP[padding],
    }),
    [padding],
  )

  // A default card belongs to the shared material system.  Explicit semantic
  // fills (warning, success, destructive, etc.) deliberately stay opaque: a
  // status must never become ambiguous because wallpaper shows through it.
  // The legacy `className` path stays as a plain View because NativeWind owns
  // its style resolution and cannot safely be forwarded into a native glass
  // host without changing existing class semantics.
  const content =
    backgroundColor || className ? (
      <View style={solidContainerStyle} className={cn(className)}>
        {children}
      </View>
    ) : (
      <GlassSurface
        tone="regular"
        interactive={Boolean(onPress) && !disabled}
        radius={RADIUS_MAP[radius]}
        style={glassContainerStyle}
        contentStyle={glassContentStyle}
      >
        {children}
      </GlassSurface>
    )

  if (!onPress) return content

  return (
    <Animated.View style={animatedStyle}>
      <Pressable
        onPress={onPress}
        onPressIn={onPressIn}
        onPressOut={onPressOut}
        disabled={disabled}
        accessibilityRole="button"
      >
        {content}
      </Pressable>
    </Animated.View>
  )
}
